import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "@/store";
import { logoutUser } from "@/store/slices/authSlice";
import Logo from "./Logo";

export default function Header() {
  const [showBanner, setShowBanner] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  
  const { isAuthenticated, userType } = useAppSelector((state) => state.auth);

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const isHomePage = location.pathname === "/";
  const isInHeroSection = isHomePage && !isScrolled;

  useEffect(() => {
    // Use Intersection Observer to detect when FeaturedProperties section is visible
    const featuredPropertiesSection = document.getElementById('featured-properties-section');
    
    if (!featuredPropertiesSection) {
      // Fallback to scroll-based detection if section not found
      const handleScroll = () => {
        setIsScrolled(window.scrollY > 400);
      };
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // Change to primary color when section starts entering viewport from top
        // Change back to white when scrolling back up and section is below viewport
        const sectionTop = entry.boundingClientRect.top;
        
        // If section top is at or above viewport top (with 10px buffer), we've scrolled to/past it
        // If section top is below viewport, we're still in hero section
        setIsScrolled(sectionTop <= 100);
      },
      {
        // Check continuously as the section moves
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1],
        rootMargin: '0px 0px 0px 0px'
      }
    );

    observer.observe(featuredPropertiesSection);

    // Also add scroll listener for more precise control
    const handleScroll = () => {
      const rect = featuredPropertiesSection.getBoundingClientRect();
      // Change color 10px before the top of featured section reaches the top of viewport
      setIsScrolled(rect.top <= 100);
    };

    window.addEventListener('scroll', handleScroll);
    // Initial check
    handleScroll();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location.pathname]);

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate("/");
    setMobileMenuOpen(false);
  };

  const getDashboardPath = () => {
    switch (userType) {
      case 'customer':
        return '/dashboard/customer';
      case 'agent':
        return '/dashboard/agent';
      case 'admin':
        return '/dashboard/admin';
      default:
        return '/dashboard';
    }
  };

  return (
    <header className="w-full fixed top-0 left-0 right-0 z-50">
      {/* Top Banner - Hidden on mobile */}
      {showBanner && (
        <div className="hidden md:block relative bg-white px-4 py-2 overflow-hidden">
          {/* Background Abstract Design */}
          <img
            src="https://api.builder.io/api/v1/image/assets/TEMP/8a454d766feff57f05d07a0e245fbb41069610a5?width=3840"
            alt=""
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-[1920px] h-auto pointer-events-none opacity-100"
            style={{ transform: "translate(-50%, calc(-50% - 610px))" }}
          />
          
          <div className="relative max-w-[1600px] mx-auto flex items-center justify-center gap-2.5">
            <p className="text-primary lg:text-center font-medium text-xs sm:text-sm leading-[150%]">
              ✨Discover Your Dream Property with Alkina Realty
            </p>
            <a href="#" className="text-primary text-center font-medium text-xs sm:text-sm leading-[150%] underline hidden sm:inline">
              Learn More
            </a>
            
            {/* Close Button */}
            <button
              onClick={() => setShowBanner(false)}
              className="absolute right-0 sm:right-4 top-1/2 -translate-y-1/2 flex items-center justify-center p-0.5 bg-gray-100 hover:bg-gray-200 transition-colors"
              aria-label="Close banner"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6 18L18 6M6 6L18 18"
                  stroke="var(--color-primary)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Navigation Bar */}
      <nav className="bg-transparent backdrop-blur-md px-4 py-4 ">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <Logo />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-[20px]">
              <Link
                to="/"
                className={`px-4 py-2 ${isInHeroSection ? 'text-white' : 'text-primary'} font-medium text-sm leading-[150%] transition-colors ${
                  isActive("/")
                    ? "border border-primary/30 bg-primary/20"
                    : "border border-transparent hover:text-card-primary"
                }`}
              >
                Home
              </Link>
              <Link
                to="/about"
                className={`px-4 py-2 ${isInHeroSection ? 'text-white' : 'text-primary'} font-medium text-sm leading-[150%] transition-colors ${
                  isActive("/about")
                    ? "border border-primary/30 bg-primary/20"
                    : "border border-transparent hover:text-card-primary"
                }`}
              >
                About Us
              </Link>
              <Link
                to="/properties"
                className={`px-4 py-2 ${isInHeroSection ? 'text-white' : 'text-primary'} font-medium text-sm leading-[150%] transition-colors ${
                  isActive("/properties")
                    ? "border border-primary/30 bg-primary/20"
                    : "border border-transparent hover:text-card-primary"
                }`}
              >
                Properties
              </Link>
              <Link
                to="/agents"
                className={`px-4 py-2 ${isInHeroSection ? 'text-white' : 'text-primary'} font-medium text-sm leading-[150%] transition-colors ${
                  isActive("/agents")
                    ? "border border-primary/30 bg-primary/20"
                    : "border border-transparent hover:text-card-primary"
                }`}
              >
                Agents
              </Link>
            </div>

            {/* Desktop Auth & Contact Buttons */}
            <div className="hidden lg:flex items-center gap-3">
              {isAuthenticated ? (
                <>
                  <Link
                    to={getDashboardPath()}
                    className={`px-3 py-1.5 ${isInHeroSection ? 'text-white' : 'text-primary'} font-medium text-xs leading-[150%] border border-white/20 bg-white/10 hover:bg-white/20 transition-colors`}
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={handleLogout}
                    className={`px-3 py-1.5 ${isInHeroSection ? 'text-white' : 'text-primary'} font-medium text-xs leading-[150%] border border-white/20 hover:bg-white/10 transition-colors`}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/auth/login"
                  className={`px-3 py-1.5 ${isInHeroSection ? 'text-white' : 'text-primary'} font-medium text-xs leading-[150%] border border-white/20 hover:bg-white/10 transition-colors`}
                >
                  Login
                </Link>
              )}
              <Link
                to="/contact"
                className={`px-3 py-1.5 ${isInHeroSection ? 'bg-white text-primary' : 'bg-primary text-white'} font-medium text-xs leading-[150%] transition-colors hover:opacity-90`}
              >
                Contact Us
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden flex items-center justify-center p-2 bg-white/10 border border-white/20 hover:bg-white/20 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 18L18 6M6 6L18 18"
                    stroke="var(--color-primary)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 12H21M3 6H21M3 18H21"
                    stroke="var(--color-primary)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden mt-3 pt-3 border-t border-white/20 space-y-1.5">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2 text-primary font-medium text-sm transition-colors ${
                  isActive("/")
                    ? "bg-primary/20 border border-primary/30"
                    : "hover:bg-white/10"
                }`}
              >
                Home
              </Link>
              <Link
                to="/about"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2 text-primary font-medium text-sm transition-colors ${
                  isActive("/about")
                    ? "bg-primary/20 border border-primary/30"
                    : "hover:bg-white/10"
                }`}
              >
                About Us
              </Link>
              <Link
                to="/properties"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2 text-primary font-medium text-sm transition-colors ${
                  isActive("/properties")
                    ? "bg-primary/20 border border-primary/30"
                    : "hover:bg-white/10"
                }`}
              >
                Properties
              </Link>
              <Link
                to="/agents"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-3 py-2 text-primary font-medium text-sm transition-colors ${
                  isActive("/agents")
                    ? "bg-primary/20 border border-primary/30"
                    : "hover:bg-white/10"
                }`}
              >
                Agents
              </Link>
              
              {/* Mobile Auth Buttons */}
              {isAuthenticated ? (
                <>
                  <Link
                    to={getDashboardPath()}
                    onClick={() => setMobileMenuOpen(false)}
                    className="block px-3 py-2 text-primary font-medium text-sm border border-white/20 bg-white/10 hover:bg-white/20 transition-colors text-center"
                  >
                    Dashboard
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full px-3 py-2 text-primary font-medium text-sm border border-white/20 hover:bg-white/10 transition-colors text-center"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/auth/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2 text-primary font-medium text-sm border border-white/20 hover:bg-white/10 transition-colors text-center"
                >
                  Login
                </Link>
              )}
              
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="block px-3 py-2 bg-primary text-white font-medium text-sm transition-colors text-center hover:opacity-90"
              >
                Contact Us
              </Link>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}

